import { ItemUnitEnum } from '@/gen/cortado/v1/settings_pb'
import { formatCurrency, formatTaxRateFraction } from '@/lib/currency-utils'
import { cn } from '@/lib/utils'
import { formatQuoteBuilderPreviewUnitLine } from '@/components/quotes/quote-builder-preview'

export type InvoiceEditorPreviewLine = {
  key: string
  name: string
  amountCents: number
  quantity: number
  unitPriceCents: number
  unit: ItemUnitEnum
  salesTaxRate?: number
  isTaxable?: boolean
  salesTaxCents?: number
}

export type InvoiceEditorPreviewSchedule = {
  key: string
  label: string
  amountCents: number
}

export interface InvoiceEditorPreviewProps {
  recipientFirstName: string
  title: string
  lineItems: InvoiceEditorPreviewLine[]
  subtotalCents: number
  discountAmountCents: number
  discountLabel?: string
  taxAmountCents: number
  tipCents: number
  tipLabel?: string
  totalCents: number
  amountDueCents: number
  schedules: InvoiceEditorPreviewSchedule[]
}

function Money({ cents, className }: { cents: number; className?: string }) {
  return <span className={cn('tabular-nums', className)}>${formatCurrency(cents)}</span>
}

function PreviewLineItem({ line }: { line: InvoiceEditorPreviewLine }) {
  const salesTaxCents = line.salesTaxCents ?? 0

  return (
    <li className="flex items-start justify-between gap-4 border-b border-border/40 py-2 last:border-0 last:pb-0">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{line.name || 'Untitled item'}</p>
        <div className="mt-0.5 flex items-center gap-2">
          <p className="font-financial text-xs text-muted-foreground">
            {formatQuoteBuilderPreviewUnitLine(line)}
          </p>
          {line.salesTaxRate !== undefined && line.salesTaxRate > 0 && (
            <span className="text-xs text-muted-foreground">
              ({formatTaxRateFraction(line.salesTaxRate)}% tax)
            </span>
          )}
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end">
        <Money cents={line.amountCents} className="font-financial text-sm font-medium" />
        {line.isTaxable && salesTaxCents > 0 && (
          <span className="font-financial text-xs text-muted-foreground">
            + ${formatCurrency(salesTaxCents)} tax
          </span>
        )}
      </div>
    </li>
  )
}

/**
 * Read-only mirror of the customer portal invoice view, driven entirely by
 * live invoice-editor form state. Nothing here is interactive: the pay button
 * is decorative so it can never be focused or activated from the editor.
 */
export function InvoiceEditorPreview({
  recipientFirstName,
  title,
  lineItems,
  subtotalCents,
  discountAmountCents,
  discountLabel,
  taxAmountCents,
  tipCents,
  tipLabel,
  totalCents,
  amountDueCents,
  schedules,
}: InvoiceEditorPreviewProps) {
  const payLabel = amountDueCents <= 0
    ? 'Paid in Full'
    : `Pay — $${formatCurrency(amountDueCents)}`

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          Exactly what {recipientFirstName} sees · updates as you type
        </p>
        <span className="rounded-full border border-emerald-400/40 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-emerald-700">
          LIVE
        </span>
      </div>

      <div className="rounded-2xl border bg-card p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-lg font-semibold">{title.trim() || 'Untitled invoice'}</h3>
          <span className="shrink-0 rounded-full bg-primary/15 px-2 py-0.5 text-xs font-medium text-primary">
            Invoice
          </span>
        </div>

        <div className="mt-4 rounded-lg border bg-card p-4">
          <p className="mb-3 text-sm font-medium">Items</p>
          <ul>
            {lineItems.length === 0 ? (
              <li className="text-sm text-muted-foreground">No line items yet</li>
            ) : (
              lineItems.map((line) => <PreviewLineItem key={line.key} line={line} />)
            )}
          </ul>
        </div>

        <div className="mt-4 space-y-2 text-sm">
          <div className="flex justify-between gap-4">
            <span>Subtotal</span>
            <Money cents={subtotalCents} className="font-financial" />
          </div>
          {discountAmountCents > 0 && (
            <div className="flex justify-between gap-4 text-emerald-700">
              <span>{discountLabel || 'Discount'}</span>
              <span className="font-financial tabular-nums">
                −${formatCurrency(discountAmountCents)}
              </span>
            </div>
          )}
          {taxAmountCents > 0 && (
            <div className="flex justify-between gap-4">
              <span>Tax</span>
              <Money cents={taxAmountCents} className="font-financial" />
            </div>
          )}
          {tipCents > 0 && (
            <div className="flex justify-between gap-4">
              <span>{tipLabel || 'Tip'}</span>
              <Money cents={tipCents} className="font-financial" />
            </div>
          )}
        </div>

        <div className="mt-2 flex justify-between gap-4 border-t-2 border-foreground pt-3 text-lg font-bold">
          <span>Total</span>
          <Money cents={totalCents} className="font-financial" />
        </div>

        {schedules.length > 0 && (
          <div className="mt-4 space-y-3">
            <h3 className="text-sm font-semibold">Payment Schedule</h3>
            <div className="space-y-2">
              {schedules.map((schedule) => (
                <div
                  key={schedule.key}
                  className="flex justify-between gap-4 text-sm text-muted-foreground"
                >
                  <span>{schedule.label}</span>
                  <Money cents={schedule.amountCents} className="font-medium text-foreground" />
                </div>
              ))}
            </div>
          </div>
        )}

        <div
          aria-hidden="true"
          className="mt-4 w-full rounded-lg bg-foreground px-4 py-2.5 text-center text-sm font-medium text-background"
        >
          {payLabel}
        </div>
      </div>
    </div>
  )
}
